import {inject, Injectable, OnDestroy} from "@angular/core";
import {ConfigurationModel} from '../configuration/configuration.model';

/**
 * Runs the Google Maps API inside a sandboxed iframe with an opaque origin, so a compromised script
 * cannot reach the main app's DOM - in particular, it cannot reach the payment page. All communication
 * happens via postMessage.
 *
 * The bridge document lives as a static asset, assets/bridges/external-api-bridge.html, which the edge
 * is configured to exempt from the app's PCI-mandated CSP.
 */
const BRIDGE_SOURCE: string = "woa-external-api-bridge";
const BRIDGE_RESPONSE_SOURCE: string = "woa-external-api-bridge-response";

export interface LatLngBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

export interface LatLng {
    lat: number;
    lng: number;
}

export interface PlacePrediction {
    description: string;
    placeId: string;
    primaryType: string;
    primaryTermValue: string;
}

export interface GeocodeResult {
    location: LatLng;
    addressComponentTypes: string[][];
}

export interface DistanceResult {
    ok: boolean;
    distanceMeters?: number;
}

interface PendingRequest {
    resolve: (data: any) => void;
    reject: (error: Error) => void;
}

/**
 * Not root-provided - deliberately scoped to whichever component tree actually needs it, so its iframe
 * is torn down (see ngOnDestroy) once that flow is no longer active rather than for the app's lifetime.
 */
@Injectable()
export class ExternalApiBridgeService implements OnDestroy {
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private iframe: HTMLIFrameElement;
    private ready: Promise<void>;
    private mapsConfigured: Promise<any>;
    private nextMessageId: number = 0;
    private readonly pending: Map<number, PendingRequest> = new Map<number, PendingRequest>();

    ngOnDestroy(): void {
        window.removeEventListener("message", this.handleResponse);
        this.iframe?.remove();
    }

    public async getPlacePredictions(input: string, country: string, bounds?: LatLngBounds): Promise<PlacePrediction[]> {
        await this.ensureMapsConfigured();
        return this.send("maps.getPlacePredictions", {input, country, bounds});
    }

    public async geocode(request: {address: string} | {placeId: string}): Promise<GeocodeResult> {
        await this.ensureMapsConfigured();
        return this.send("maps.geocode", request);
    }

    public async getDistances(origin: LatLng, destinations: LatLng[]): Promise<DistanceResult[]> {
        await this.ensureMapsConfigured();
        return this.send("maps.getDistances", {origin, destinations});
    }

    /**
     * The API key is owned here, not baked into the iframe's own script - it's sent across in this one
     * dedicated call, which is also where the Maps script actually gets loaded inside the iframe.
     */
    private ensureMapsConfigured(): Promise<any> {
        if (!this.mapsConfigured) {
            this.mapsConfigured = this.send("maps.configure", {apiKey: this.configurationModel.googleMapsApiKey()});
        }
        return this.mapsConfigured;
    }

    private ensureIframe(): Promise<void> {
        if (!this.ready) {
            this.ready = new Promise<void>((resolve: () => void) => {
                this.iframe = document.createElement("iframe");
                this.iframe.sandbox.add("allow-scripts");
                this.iframe.style.display = "none";
                this.iframe.src = "assets/bridges/external-api-bridge.html";
                this.iframe.onload = () => resolve();
                document.body.appendChild(this.iframe);
            });
            window.addEventListener("message", this.handleResponse);
        }
        return this.ready;
    }

    private readonly handleResponse = (event: MessageEvent): void => {
        if (event.source !== this.iframe?.contentWindow || event.data?.source !== BRIDGE_RESPONSE_SOURCE) {
            return;
        }
        const pendingRequest: PendingRequest = this.pending.get(event.data.id);
        if (pendingRequest) {
            this.pending.delete(event.data.id);
            if (event.data.result.ok) {
                pendingRequest.resolve(event.data.result.data);
            } else {
                pendingRequest.reject(new Error(event.data.result.error));
            }
        }
    };

    private async send(type: string, payload: Record<string, any>): Promise<any> {
        await this.ensureIframe();
        const id: number = this.nextMessageId++;
        return new Promise<any>((resolve: (data: any) => void, reject: (error: Error) => void) => {
            this.pending.set(id, {resolve, reject});
            this.iframe.contentWindow.postMessage({source: BRIDGE_SOURCE, id, type, ...payload}, "*");
        });
    }
}
